import type {
    SupportAttachment,
    SupportMessage,
    SupportPerson,
    SupportRealtimeEvent,
    SupportTicketStatus,
    SupportTicketStatusFields,
    SupportTicketStatusGroup,
    SupportTicketSummary,
} from 'chaika-frontend-types/frontend-cms';

import { isSupportGatewayAvailable, SupportApi, toSupportTicketNumber } from './SupportApi';

export interface SupportRealtimeCallbacks {
    onConnected(): void;
    onDisconnected?(): void;
    onAuthorizationRequired?(): void;
    onEvent(event: SupportRealtimeEvent): void;
}

const SUPPORT_PROTOCOL = 'support.v1';
const SUPPORT_PUBLIC_PATH = '/support-ws';
const SUPPORT_LOCAL_URL = 'ws://localhost:3030/ws';
const RECONNECT_DELAYS_MS = [1_000, 2_000, 4_000, 8_000, 15_000, 30_000];
const DATE_TIME_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const MAX_STATUS_NAME_LENGTH = 100;
const IGNORED_EVENT = 'ignored';

export class SupportRealtimeClient {
    private socket?: WebSocket;
    private reconnectTimer?: ReturnType<typeof setTimeout>;
    private connectionSequence = 0;
    private reconnectAttempt = 0;
    private hasRetriedAuthorization = false;
    private isClosed = true;
    private isReconnectStopped = false;
    public constructor(private readonly callbacks: SupportRealtimeCallbacks) {}

    public connect(): void {
        if (!this.isClosed || !isSupportGatewayAvailable()) {
            return;
        }

        this.isClosed = false;
        this.hasRetriedAuthorization = false;
        this.reconnectAttempt = 0;
        this.isReconnectStopped = false;
        window.addEventListener('online', this.handleOnline);
        void this.openSocket();
    }

    public close(): void {
        this.isClosed = true;
        this.connectionSequence += 1;
        this.clearReconnectTimer();
        window.removeEventListener('online', this.handleOnline);

        const socket = this.socket;
        this.socket = undefined;
        if (socket && socket.readyState !== WebSocket.CLOSED) {
            socket.close(1000, 'Support widget closed');
        }
    }

    private readonly handleOnline = (): void => {
        if (this.isClosed || this.isReconnectStopped) return;
        this.reconnectAttempt = 0;
        if (!this.socket) {
            this.clearReconnectTimer();
            void this.openSocket();
        }
    };

    private async openSocket(): Promise<void> {
        const connectionSequence = ++this.connectionSequence;

        try {
            const token = await SupportApi.getFreshAccessToken();
            if (this.isClosed || connectionSequence !== this.connectionSequence) {
                return;
            }

            const protocols = token ? [SUPPORT_PROTOCOL, `bearer.${token}`] : [SUPPORT_PROTOCOL];
            const socket = new WebSocket(getSupportUrl(), protocols);
            this.socket = socket;
            socket.onopen = () => this.handleOpen(socket);
            socket.onmessage = event => this.handleMessage(socket, event.data);
            socket.onclose = event => this.handleClose(socket, event.code);
            socket.onerror = () => console.error('[SupportRealtime] WebSocket transport error');
        } catch (error) {
            if (this.isClosed || connectionSequence !== this.connectionSequence) {
                return;
            }

            console.error('[SupportRealtime] Failed to obtain access token', error);
            this.scheduleReconnect();
        }
    }

    private handleOpen(socket: WebSocket): void {
        if (socket !== this.socket || this.isClosed) {
            return;
        }
        if (socket.protocol !== SUPPORT_PROTOCOL) {
            socket.close(1002, 'Unexpected WebSocket protocol');
            return;
        }

        this.reconnectAttempt = 0;
        this.callbacks.onConnected();
    }

    private handleMessage(socket: WebSocket, serializedEvent: unknown): void {
        if (socket !== this.socket || this.isClosed) {
            return;
        }

        const event = parseSupportEvent(serializedEvent);
        if (event === IGNORED_EVENT) {
            return;
        }
        if (!event) {
            console.error('[SupportRealtime] Ignored invalid or unknown event');
            return;
        }

        this.callbacks.onEvent(event);
    }

    private handleClose(socket: WebSocket, closeCode: number): void {
        if (socket !== this.socket) {
            return;
        }

        this.socket = undefined;
        this.callbacks.onDisconnected?.();
        if (this.isClosed || closeCode === 1000 || closeCode === 1002) {
            this.isReconnectStopped = true;
            return;
        }
        if (closeCode === 1008) {
            if (this.hasRetriedAuthorization) {
                console.error('[SupportRealtime] Authorization was revoked after a fresh-token retry');
                this.isReconnectStopped = true;
                this.callbacks.onAuthorizationRequired?.();
                return;
            }

            this.hasRetriedAuthorization = true;
            void this.openSocket();
            return;
        }

        this.hasRetriedAuthorization = false;
        if (![1001, 1006, 1011].includes(closeCode)) {
            console.error(`[SupportRealtime] Reconnecting after unknown close code ${closeCode}`);
        }
        this.scheduleReconnect();
    }

    private scheduleReconnect(): void {
        if (this.isClosed || this.reconnectTimer) {
            return;
        }

        const delayIndex = Math.min(this.reconnectAttempt, RECONNECT_DELAYS_MS.length - 1);
        const baseDelay = RECONNECT_DELAYS_MS[delayIndex];
        const delayWithJitter = Math.round(baseDelay * (0.5 + Math.random()));
        const reconnectDelay =
            baseDelay === RECONNECT_DELAYS_MS[RECONNECT_DELAYS_MS.length - 1]
                ? Math.max(baseDelay, delayWithJitter)
                : delayWithJitter;
        this.reconnectAttempt += 1;
        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = undefined;
            void this.openSocket();
        }, reconnectDelay);
    }

    private clearReconnectTimer(): void {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = undefined;
        }
    }
}

function getSupportUrl(): string {
    if (process.env.CMS_ENV === 'local-dev') {
        return SUPPORT_LOCAL_URL;
    }

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.host}${SUPPORT_PUBLIC_PATH}`;
}

function parseSupportEvent(serializedEvent: unknown): SupportRealtimeEvent | typeof IGNORED_EVENT | undefined {
    if (typeof serializedEvent !== 'string') {
        return undefined;
    }

    let value: unknown;
    try {
        value = JSON.parse(serializedEvent);
    } catch {
        return undefined;
    }

    if (!hasKeys(value, ['type', 'payload']) || typeof value.type !== 'string' || !isRecord(value.payload)) {
        return undefined;
    }

    const { payload } = value;
    switch (value.type) {
        case 'message.created':
            return hasKeys(payload, ['ticketId', 'message']) &&
                isUuid(payload.ticketId) &&
                isSupportMessage(payload.message)
                ? {
                      type: 'message.created',
                      payload: { ticketId: payload.ticketId, message: toSupportMessage(payload.message) },
                  }
                : undefined;
        case 'message.updated':
            return hasKeys(payload, ['ticketId', 'message']) &&
                isUuid(payload.ticketId) &&
                isSupportMessage(payload.message) &&
                typeof payload.message.editedAt === 'string'
                ? {
                      type: 'message.updated',
                      payload: {
                          ticketId: payload.ticketId,
                          message: { ...toSupportMessage(payload.message), editedAt: payload.message.editedAt },
                      },
                  }
                : undefined;
        case 'message.deleted':
            return hasKeys(payload, ['ticketId', 'messageId']) && isUuid(payload.ticketId) && isUuid(payload.messageId)
                ? { type: 'message.deleted', payload: { ticketId: payload.ticketId, messageId: payload.messageId } }
                : undefined;
        case 'message.reactions_updated':
            return IGNORED_EVENT;
        case 'ticket.status_changed':
            return hasKeys(payload, ['ticketId']) && isUuid(payload.ticketId) && hasSupportTicketStatusFields(payload)
                ? {
                      type: 'ticket.status_changed',
                      payload: {
                          ticketId: payload.ticketId,
                          status: payload.status,
                          statusName: payload.statusName,
                          statusGroup: payload.statusGroup,
                      },
                  }
                : undefined;
        case 'ticket.responsible_changed':
            return hasKeys(payload, ['ticketId', 'responsible']) &&
                isUuid(payload.ticketId) &&
                (payload.responsible === null || isSupportPerson(payload.responsible))
                ? {
                      type: 'ticket.responsible_changed',
                      payload: {
                          ticketId: payload.ticketId,
                          responsible: payload.responsible && toSupportPerson(payload.responsible),
                      },
                  }
                : undefined;
        case 'ticket.added':
            return hasKeys(payload, ['ticket']) && isSupportTicketSummary(payload.ticket)
                ? { type: 'ticket.added', payload: { ticket: toSupportTicketSummary(payload.ticket) } }
                : undefined;
        case 'ticket.removed':
            return hasKeys(payload, ['ticketId']) && isUuid(payload.ticketId)
                ? { type: 'ticket.removed', payload: { ticketId: payload.ticketId } }
                : undefined;
        case 'unread.updated':
            return hasKeys(payload, ['ticketId', 'count', 'totalUnread']) &&
                isUuid(payload.ticketId) &&
                isNonnegativeInteger(payload.count) &&
                isNonnegativeInteger(payload.totalUnread)
                ? {
                      type: 'unread.updated',
                      payload: { ticketId: payload.ticketId, count: payload.count, totalUnread: payload.totalUnread },
                  }
                : undefined;
        default:
            return undefined;
    }
}

function isSupportMessage(
    value: unknown,
): value is Record<string, unknown> & Omit<SupportMessage, 'authorId'> & { authorId?: string | null } {
    return (
        hasKeys(value, ['id', 'author', 'authorName', 'text', 'attachments', 'createdAt']) &&
        isUuid(value.id) &&
        (value.author === 'doctor' || value.author === 'support') &&
        (value.authorId === undefined || value.authorId === null || isUuid(value.authorId)) &&
        typeof value.authorName === 'string' &&
        typeof value.text === 'string' &&
        Array.isArray(value.attachments) &&
        value.attachments.every(isSupportAttachment) &&
        isDateTime(value.createdAt) &&
        (value.editedAt === undefined || isDateTime(value.editedAt))
    );
}

function toSupportMessage(message: Omit<SupportMessage, 'authorId'> & { authorId?: string | null }): SupportMessage {
    return {
        id: message.id,
        author: message.author,
        ...(message.authorId ? { authorId: message.authorId } : {}),
        authorName: message.authorName,
        text: message.text,
        attachments: message.attachments.map(toSupportAttachment),
        createdAt: message.createdAt,
        ...(message.editedAt ? { editedAt: message.editedAt } : {}),
    };
}

function isSupportAttachment(value: unknown): value is SupportAttachment {
    return (
        hasKeys(value, ['id', 'fileName', 'mimeType', 'size', 'url']) &&
        isUuid(value.id) &&
        typeof value.fileName === 'string' &&
        typeof value.mimeType === 'string' &&
        isNonnegativeInteger(value.size) &&
        typeof value.url === 'string'
    );
}

function toSupportAttachment({ id, fileName, mimeType, size, url }: SupportAttachment): SupportAttachment {
    return { id, fileName, mimeType, size, url };
}

function isSupportTicketSummary(
    value: unknown,
): value is Record<string, unknown> & Omit<SupportTicketSummary, 'number'> {
    return (
        hasKeys(value, ['id', 'title', 'responsible', 'unread', 'lastMessageAt']) &&
        isUuid(value.id) &&
        typeof value.title === 'string' &&
        hasSupportTicketStatusFields(value) &&
        (value.responsible === null || isSupportPerson(value.responsible)) &&
        isNonnegativeInteger(value.unread) &&
        (value.lastMessageAt === null || isDateTime(value.lastMessageAt))
    );
}

function toSupportTicketSummary(
    ticket: Record<string, unknown> & Omit<SupportTicketSummary, 'number'>,
): SupportTicketSummary {
    return {
        id: ticket.id,
        number: toSupportTicketNumber(ticket.number),
        title: ticket.title,
        status: ticket.status,
        statusName: ticket.statusName,
        statusGroup: ticket.statusGroup,
        responsible: ticket.responsible && toSupportPerson(ticket.responsible),
        unread: ticket.unread,
        lastMessageAt: ticket.lastMessageAt,
    };
}

function hasSupportTicketStatusFields(
    value: Record<string, unknown>,
): value is Record<string, unknown> & SupportTicketStatusFields {
    return (
        isSupportTicketStatus(value.status) &&
        typeof value.statusName === 'string' &&
        value.statusName.length > 0 &&
        value.statusName.length <= MAX_STATUS_NAME_LENGTH &&
        (value.statusGroup === null || isSupportTicketStatusGroup(value.statusGroup))
    );
}

function isSupportPerson(value: unknown): value is SupportPerson {
    return hasKeys(value, ['id', 'name']) && typeof value.id === 'string' && typeof value.name === 'string';
}

function toSupportPerson({ id, name }: SupportPerson): SupportPerson {
    return { id, name };
}

function isSupportTicketStatus(value: unknown): value is SupportTicketStatus {
    return (
        value === 'new' ||
        value === 'in_progress' ||
        value === 'in_development' ||
        value === 'done' ||
        value === 'deleted'
    );
}

function isSupportTicketStatusGroup(value: unknown): value is SupportTicketStatusGroup {
    return value === 'to_do' || value === 'in_progress' || value === 'complete';
}

function hasKeys(value: unknown, keys: string[]): value is Record<string, unknown> {
    return isRecord(value) && keys.every(key => Object.prototype.hasOwnProperty.call(value, key));
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isUuid(value: unknown): value is string {
    return typeof value === 'string' && UUID_PATTERN.test(value);
}

function isDateTime(value: unknown): value is string {
    return typeof value === 'string' && DATE_TIME_PATTERN.test(value) && !Number.isNaN(Date.parse(value));
}

function isNonnegativeInteger(value: unknown): value is number {
    return typeof value === 'number' && Number.isInteger(value) && value >= 0;
}
